import { Assets, Container, Sprite } from 'pixi.js';
import type { Texture } from 'pixi.js';
import { PROTOCOL_VERSION, type ServerMessage } from '@nanocars/shared';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH, type Scene, type SceneManager } from './SceneManager';
import { UI_PATHS } from '../assets/manifest';
import { COLORS } from '../ui/theme';
import { makeImageButton, makeLabel, makeTextInput, type TextInputHandle } from '../ui/widgets';
import { FocusRing } from '../ui/focus';
import { session, wsUrl } from '../state/Session';
import { JoinScene } from './JoinScene';
import { LobbyScene } from './LobbyScene';

const RECONNECT_INTERVAL_S = 2;

// Landing scene: logo, name entry, Create Room / Join Room, and an input
// hint (keyboard or gamepad icon, whichever Input last saw activity from).
export class TitleScene implements Scene {
  readonly name = 'title';
  readonly container = new Container();

  private nameInput: TextInputHandle | null = null;
  private createBtn: Container | null = null;
  private joinBtn: Container | null = null;
  private connectingLabel: ReturnType<typeof makeLabel> | null = null;
  private errorLabel: ReturnType<typeof makeLabel> | null = null;
  private hintIcon: Sprite | null = null;
  private hintLabel: ReturnType<typeof makeLabel> | null = null;
  private keysTexture: Texture | null = null;
  private padTexture: Texture | null = null;
  private focusRing: FocusRing | null = null;
  private unsubscribers: Array<() => void> = [];
  private reconnectTimer = 0;
  private wasConnected: boolean | null = null;

  constructor(private readonly scenes: SceneManager) {}

  async enter(): Promise<void> {
    const logoTexture = await Assets.load(UI_PATHS.logo);
    const buttonTexture = await Assets.load(UI_PATHS.button);
    const keysTexture = await Assets.load(UI_PATHS.icon_keys);
    const padTexture = await Assets.load(UI_PATHS.icon_pad);
    this.keysTexture = keysTexture;
    this.padTexture = padTexture;

    const logo = new Sprite(logoTexture);
    logo.anchor.set(0.5, 0);
    logo.width = 480;
    logo.height = 480 * (logoTexture.height / logoTexture.width);
    logo.x = LOGICAL_WIDTH / 2;
    logo.y = 30;
    this.container.addChild(logo);

    const nameLabel = makeLabel('Name', { fontSize: 18, fill: COLORS.textLight });
    nameLabel.x = LOGICAL_WIDTH / 2 - 160;
    nameLabel.y = 254;
    this.container.addChild(nameLabel);

    this.nameInput = makeTextInput('Driver', 16, { initialValue: session.name });
    this.nameInput.container.x = LOGICAL_WIDTH / 2 - 160;
    this.nameInput.container.y = 280;
    this.container.addChild(this.nameInput.container);

    this.createBtn = makeImageButton(buttonTexture, 'Create Room', () => this.handleCreate());
    this.createBtn.x = LOGICAL_WIDTH / 2 - 110;
    this.createBtn.y = 358;

    this.joinBtn = makeImageButton(buttonTexture, 'Join Room', () => this.handleJoin());
    this.joinBtn.x = LOGICAL_WIDTH / 2 - 110;
    this.joinBtn.y = 452;

    this.container.addChild(this.createBtn, this.joinBtn);

    this.connectingLabel = makeLabel('Connecting...', { fontSize: 22, fill: COLORS.textLight });
    this.connectingLabel.anchor.set(0.5);
    this.connectingLabel.x = LOGICAL_WIDTH / 2;
    this.connectingLabel.y = 405;
    this.container.addChild(this.connectingLabel);

    this.errorLabel = makeLabel('', { fontSize: 16, fill: 0xff6b5b });
    this.errorLabel.anchor.set(0.5, 0);
    this.errorLabel.x = LOGICAL_WIDTH / 2;
    this.errorLabel.y = 548;
    this.container.addChild(this.errorLabel);

    this.hintIcon = new Sprite(session.input.source === 'gamepad' ? padTexture : keysTexture);
    this.hintIcon.width = 40;
    this.hintIcon.height = 40;
    this.hintIcon.x = LOGICAL_WIDTH - 196;
    this.hintIcon.y = LOGICAL_HEIGHT - 68;
    this.container.addChild(this.hintIcon);

    this.hintLabel = makeLabel(hintText(session.input.source), { fontSize: 16, fill: COLORS.textLight });
    this.hintLabel.x = LOGICAL_WIDTH - 146;
    this.hintLabel.y = LOGICAL_HEIGHT - 54;
    this.container.addChild(this.hintLabel);

    this.focusRing = new FocusRing(this.container, session.input);

    this.unsubscribers.push(
      session.net.on('welcome', (msg) => this.handleWelcome(msg)),
      session.net.on('error', (msg) => this.handleError(msg)),
    );

    this.applyConnectionState();
  }

  exit(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    this.focusRing?.dispose();
    this.nameInput?.destroy();
    this.container.removeChildren();
  }

  update(dt: number): void {
    if (this.hintIcon && this.hintLabel && this.keysTexture && this.padTexture) {
      this.hintIcon.texture = session.input.source === 'gamepad' ? this.padTexture : this.keysTexture;
      this.hintLabel.text = hintText(session.input.source);
    }

    if (session.net.connected) {
      this.reconnectTimer = 0;
    } else {
      this.reconnectTimer += dt;
      if (this.reconnectTimer >= RECONNECT_INTERVAL_S) {
        this.reconnectTimer = 0;
        session.net.connect(wsUrl()).catch(() => {
          // applyConnectionState (called every frame) keeps the UI in sync;
          // a failed retry just means we try again next interval.
        });
      }
    }
    this.applyConnectionState();
  }

  resize(_w: number, _h: number): void {
    // Fixed 1280x720 logical layout; nothing depends on resize.
  }

  // Drives the `?auto=create&name=...` automation flag: waits for a
  // connection (polling, since NetClient has no "connected" event) and then
  // fires the same path Create Room's click does.
  autoCreate(): void {
    const attempt = (): void => {
      if (session.net.connected) {
        this.handleCreate();
        return;
      }
      setTimeout(attempt, 200);
    };
    attempt();
  }

  private applyConnectionState(): void {
    const connected = session.net.connected;
    if (this.connectingLabel) this.connectingLabel.visible = !connected;
    // Name input (and its label) stay visible at all times so the player
    // can type their name while the connection is still coming up - only
    // the Create/Join buttons swap for the "Connecting..." text.
    if (this.createBtn) this.createBtn.visible = connected;
    if (this.joinBtn) this.joinBtn.visible = connected;

    // Only touch focus items on an actual state transition - setItems()
    // resets focusedIndex, and applyConnectionState runs every frame, so
    // calling it unconditionally would fight keyboard/gamepad navigation.
    if (connected !== this.wasConnected) {
      this.wasConnected = connected;
      if (this.focusRing && this.createBtn && this.joinBtn) {
        this.focusRing.setItems(
          connected
            ? [
                { container: this.createBtn, activate: () => this.handleCreate() },
                { container: this.joinBtn, activate: () => this.handleJoin() },
              ]
            : [],
        );
      }
    }
  }

  private handleCreate(): void {
    if (!session.net.connected || !this.nameInput) return;
    session.name = this.nameInput.value;
    const existingSession = session.net.session;
    session.net.send({
      t: 'create',
      v: PROTOCOL_VERSION,
      name: session.name,
      ...(existingSession !== null ? { session: existingSession } : {}),
    });
  }

  private handleJoin(): void {
    void this.scenes.go(new JoinScene(this.scenes));
  }

  private handleWelcome(msg: Extract<ServerMessage, { t: 'welcome' }>): void {
    session.playerId = msg.playerId;
    session.net.session = msg.session;
    session.lobby = msg.lobby;
    void this.scenes.go(new LobbyScene(this.scenes));
  }

  private handleError(msg: Extract<ServerMessage, { t: 'error' }>): void {
    if (this.errorLabel) this.errorLabel.text = msg.msg;
  }
}

function hintText(source: 'keyboard' | 'gamepad'): string {
  return source === 'gamepad' ? 'Gamepad' : 'Keyboard';
}
